(() => {
  if (customElements.get("stories-viewer")) return;

  /* ---------- Flickity lazy loader ---------- */
  let flickityPromise;
  const loadFlickity = (src) => {
    if (window.Flickity) return Promise.resolve(window.Flickity);
    flickityPromise ||= new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.onload = () => resolve(window.Flickity);
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return flickityPromise;
  };

  /* ---------- <story-player>: one story = N items played in sequence ---------- */
  class StoryPlayer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.items = [...this.querySelectorAll("[data-item]")];
      this.imageDuration = (Number(this.dataset.duration) || 5) * 1000;
      this.index = 0;
      this.paused = false;
      this.active = false;

      // Build progress bars from the actual number of items
      const barsEl = this.querySelector("[data-bars]");
      this.fills = this.items.map(() => {
        const bar = document.createElement("span");
        bar.className = "story__bar";
        const fill = document.createElement("i");
        bar.append(fill);
        barsEl.append(bar);
        return fill;
      });

      this.querySelector("[data-toggle]").addEventListener("click", () =>
        this.togglePause(),
      );
      this.querySelector("[data-tap-prev]").addEventListener("click", () =>
        this.prev(),
      );
      this.querySelector("[data-tap-next]").addEventListener("click", () =>
        this.next(),
      );
    }

    start() {
      this.active = true;
      this.setPaused(false);
      this.show(0);
    }

    stop() {
      this.active = false;
      cancelAnimationFrame(this.raf);
      this.items.forEach((el) => el.querySelector("video")?.pause());
    }

    show(i) {
      this.index = i;
      this.elapsed = 0;
      this.lastTs = 0;

      this.items.forEach((el, n) => {
        el.hidden = n !== i;
        if (n !== i) el.querySelector("video")?.pause();
      });
      this.fills.forEach(
        (fill, n) => (fill.style.transform = `scaleX(${n < i ? 1 : 0})`),
      );

      this.video = this.items[i].querySelector("video");
      if (this.video) {
        this.video.currentTime = 0;
        if (!this.paused) this.video.play().catch(() => {});
      }

      // Warm up the next image
      this.items[i + 1]?.querySelector("img")?.setAttribute("loading", "eager");

      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(this.tick);
    }

    tick = (ts) => {
      if (!this.active) return;
      const dt = this.lastTs ? Math.min(ts - this.lastTs, 100) : 0;
      this.lastTs = ts;

      let progress;
      if (this.video) {
        const v = this.video;
        progress = v.ended ? 1 : v.duration ? v.currentTime / v.duration : 0;
      } else {
        const img = this.items[this.index].querySelector("img");
        const loaded = !img || (img.complete && img.naturalWidth > 0);
        if (loaded && !this.paused) this.elapsed += dt; // timer starts once the media is loaded
        progress = Math.min(this.elapsed / this.imageDuration, 1);
      }

      this.fills[this.index].style.transform = `scaleX(${progress})`;

      if (progress >= 1) return this.next();
      this.raf = requestAnimationFrame(this.tick);
    };

    next() {
      if (this.index < this.items.length - 1) this.show(this.index + 1);
      else this.dispatchEvent(new CustomEvent("story:end", { bubbles: true }));
    }

    prev() {
      if (this.index > 0) this.show(this.index - 1);
      else this.dispatchEvent(new CustomEvent("story:back", { bubbles: true }));
    }

    togglePause() {
      this.setPaused(!this.paused);
    }

    setPaused(value) {
      this.paused = value;
      this.classList.toggle("is-paused", value);
      if (this.video)
        value ? this.video.pause() : this.video.play().catch(() => {});
      this.lastTs = 0;
    }
  }

  /* ---------- <stories-viewer>: thumbnails + popup + Flickity ---------- */
  class StoriesViewer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.dialog = this.querySelector("dialog");
      this.carousel = this.querySelector("[data-carousel]");
      this.players = [...this.carousel.querySelectorAll("story-player")];
      this.current = null;

      this.querySelectorAll("[data-open]").forEach((btn) =>
        btn.addEventListener("click", () =>
          this.open(Number(btn.dataset.open)),
        ),
      );

      this.querySelector("[data-close]").addEventListener("click", () =>
        this.dialog.close(),
      );
      this.dialog.addEventListener("close", () => this.onClosed());
      this.dialog.addEventListener("click", (e) => {
        if (e.target === this.dialog) this.dialog.close(); // backdrop click
      });

      this.initSlider(0);

      // white circles => story ELEMENTS
      this.querySelector("[data-item-prev]").addEventListener("click", () =>
        this.current?.prev(),
      );
      this.querySelector("[data-item-next]").addEventListener("click", () =>
        this.current?.next(),
      );
      // skip icons => STORIES (Flickity slides)
      this.querySelector("[data-story-prev]").addEventListener("click", () =>
        this.flkty?.previous(),
      );
      this.querySelector("[data-story-next]").addEventListener("click", () =>
        this.flkty?.next(),
      );

      // events coming from <story-player>
      this.addEventListener("story:end", () => {
        if (this.flkty.selectedIndex < this.players.length - 1)
          this.flkty.next();
        else this.dialog.close();
      });
      this.addEventListener("story:back", (e) => {
        if (this.flkty.selectedIndex > 0) this.flkty.previous();
        else e.target.start(); // first item of first story: restart
      });

      this.dialog.addEventListener("keydown", (e) => {
        if (e.key === "ArrowRight") this.current?.next();
        if (e.key === "ArrowLeft") this.current?.prev();
        if (e.key === " " && e.target === this.dialog) {
          e.preventDefault();
          this.current?.togglePause();
        }
      });
    }

    async open(index) {
      this.dialog.showModal();
      document.documentElement.style.overflow = "hidden";

      if (!this.flkty) {
        this.initSlider(index);
      } else {
        this.flkty.resize();
        this.flkty.select(index, false, true); // instant jump, no animation
      }
      this.activate();
    }

    initSlider(index) {
      this.flkty = new Flickity(this.carousel, {
        cellSelector: "story-player",
        cellAlign: "center",
        initialIndex: index,
        prevNextButtons: false,
        pageDots: false,
        setGallerySize: false,
        contain: false,
        wrapAround: false,
        draggable: true,
      });
      this.flkty.on("select", () => this.activate());
    }

    activate() {
      const player = this.players[this.flkty.selectedIndex];
      this.querySelector("[data-story-prev]").disabled =
        this.flkty.selectedIndex === 0;
      this.querySelector("[data-story-next]").disabled =
        this.flkty.selectedIndex === this.players.length - 1;
      if (player === this.current) return;
      this.current?.stop();
      this.current = player;
      player.start();
    }

    onClosed() {
      this.current?.stop();
      this.current = null;
      document.documentElement.style.overflow = "";
    }
  }

  if (!customElements.get("stories-viewer")) {
    customElements.define("story-player", StoryPlayer);
    customElements.define("stories-viewer", StoriesViewer);
  }
})();
